import { KeyboardEvent, RefObject, useCallback } from 'react';
import { VariableSizeList } from 'react-window';

import { anyOptionalKeyDown } from 'flow/utils/browser-utils';

import { ListRow, RowIsFocusable, listKeyAction } from './list-key-action';
import { focusListRow, listRowIndex, listRowTarget } from './list-row-focus';

interface UseListKeysOptions<Row extends ListRow> {
    containerRef: RefObject<HTMLElement>;
    /** The react-window list, if the list is one. */
    listRef?: RefObject<VariableSizeList>;
    rows: Row[];
    rowIsFocusable: RowIsFocusable<Row>;
}

/**
 * The list's key handler, for its container. It finds the row from the
 * element that has focus, so the rows need no handler of their own. Lists get
 * it from useListTabStop, together with the container's focus handler.
 */
const useListKeys = <Row extends ListRow>({
    containerRef,
    listRef,
    rows,
    rowIsFocusable,
}: UseListKeysOptions<Row>) =>
    useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            const container = containerRef.current;
            const index = listRowIndex(container, event.target as Element);
            if (index === null) {
                return;
            }
            // Only the row's own control takes the list's keys. Keys pressed
            // in the row's other controls, such as a call button or a slider,
            // stay with them.
            const rowControl = listRowTarget(container, index);
            if (
                rowControl === null ||
                event.target !== rowControl ||
                anyOptionalKeyDown(event)
            ) {
                return;
            }

            const action = listKeyAction(
                rows,
                rowIsFocusable,
                index,
                event.key,
                getComputedStyle(rowControl).direction === 'rtl',
            );
            if (!action) {
                return;
            }

            event.preventDefault();
            if (action.kind === 'click') {
                rowControl.click();
            } else {
                focusListRow(container, listRef, action.index);
            }
        },
        [containerRef, listRef, rows, rowIsFocusable],
    );

export default useListKeys;
